import {groupBy} from 'lodash';
import RoundingUtility, {RoundingEntities} from '../../../../forecast-app/shared/util/RoundingUtil';
import {hasFeatureFlag} from '../../../../forecast-app/shared/util/FeatureUtil';

export const groupByProperties = (expenses, groupByPropertyList) => {
	groupByPropertyList = groupByPropertyList.slice(0);
	const groupByProperty = groupByPropertyList.shift();
	const groupedExpenses = groupBy(expenses, groupByProperty);
	if (groupByPropertyList.length > 0) {
		Object.keys(groupedExpenses).forEach(groupByValue => {
			groupedExpenses[groupByValue] = groupByProperties(groupedExpenses[groupByValue], groupByPropertyList);
		});
	}
	return groupedExpenses;
};

export const visitGroups = (groups, visitor, groupKeys = []) => {
	if (Array.isArray(groups)) {
		visitor(groups, groupKeys);
	} else if (typeof groups === 'object') {
		Object.keys(groups).forEach(groupKey => {
			const newGroupKeys = groupKeys.slice(0);
			newGroupKeys.push(groupKey);
			visitGroups(groups[groupKey], visitor, newGroupKeys);
		});
	}
};

export const formatDate = (intl, date) => {
	return intl.formatDate(date.startOf('day'), {
		year: 'numeric',
		month: 'short',
		day: 'numeric',
	});
};

export function getExchangeRateOnDate(edges, currency, invoiceDateStr) {
	const rates = edges
		.map(edge => edge.node)
		.filter(node => node.currency === currency)
		.sort((a, b) => {
			if (!a.startDate) return -1;
			if (!b.startDate) return 1;
			return a.startDate.localeCompare(b.startDate);
		});

	let effectiveRate;
	for (const rate of rates) {
		if (!rate.startDate || rate.startDate <= invoiceDateStr) {
			effectiveRate = rate.rate;
		}
	}
	return effectiveRate;
}

export function getCurrentDisplayRate(edges, currency) {
	const today = new Date().toISOString().slice(0, 10);
	return getExchangeRateOnDate(edges, currency, today);
}

// Returns the row with startDate == null for a currency, matching the backend's "active"
// exchange-rate convention. Unlike getCurrentDisplayRate, which walks dated history rows effective
// as of today, this intentionally ignores dated history entirely.
// The backend guarantees at most one such row per currency (creating a second is rejected), not at
// least one - a currency can end up with none (e.g. its active rate is deleted while dated rows and
// no rate card reference it). In that case this returns undefined; callers already treat a missing
// rate as "no conversion available" rather than assuming a value always exists.
export function getActiveRate(edges, currency) {
	const activeRate = edges.map(edge => edge.node).find(node => node.currency === currency && !node.startDate);
	return activeRate?.rate;
}

/**
 * Resolves a currency's exchange rate as of an invoice's date: the most recent rate effective on or
 * before that date, falling back to the most recently started rate when the invoice has no date or
 * predates every dated rate.
 *
 * Shared by the conversions in both directions so the two can never disagree about which rate
 * applies — billing and the deliverable ledger have to agree on that, and a fix applied to one copy
 * of this rule and not the other would silently reintroduce a discrepancy between them.
 */
const resolveRateForInvoice = (company, invoice) => {
	const edges = company.dateEffectiveExchangeRates?.edges ?? [];
	const invoiceDateStr = invoice?.date?.format?.('YYYY-MM-DD') ?? null;

	return currency => {
		if (invoiceDateStr && edges.length > 0) {
			const dated = getExchangeRateOnDate(edges, currency, invoiceDateStr);
			if (dated != null) return dated;
		}
		return edges
			.map(e => e.node)
			.filter(n => n.currency === currency)
			.sort((a, b) => (!b.startDate ? 1 : !a.startDate ? -1 : b.startDate.localeCompare(a.startDate)))[0]?.rate;
	};
};

export const convertPriceToInvoiceCurrency = (price, sourceCurrency, company, invoice) => {
	let priceInInvoiceCurrency;
	const hasRateRounding = hasFeatureFlag('rate_rounding');

	const getRate = resolveRateForInvoice(company, invoice);

	if (sourceCurrency !== invoice.currency) {
		let priceInCompanyCurrency;
		if (sourceCurrency !== company.currency) {
			priceInCompanyCurrency = price / getRate(sourceCurrency);
		} else {
			priceInCompanyCurrency = price;
		}

		if (invoice.currency !== company.currency) {
			priceInInvoiceCurrency = priceInCompanyCurrency * getRate(invoice.currency);
		} else {
			priceInInvoiceCurrency = priceInCompanyCurrency;
		}
	} else {
		priceInInvoiceCurrency = price;
	}

	return hasRateRounding
		? RoundingUtility.roundBasedOnEntityType(priceInInvoiceCurrency, RoundingEntities.RATE.name)
		: Math.round(priceInInvoiceCurrency * 100) / 100;
};

/**
 * The inverse of convertPriceToInvoiceCurrency: takes an amount expressed against an invoice and
 * returns it in `targetCurrency` — the project's rate-card currency, which is what the deliverable
 * ledger records.
 *
 * Always rounds to two decimals because the ledger column holds two, and deliberately does not
 * apply rate rounding: this is a money amount, not a rate.
 */
export const convertPriceFromInvoiceCurrency = (priceInInvoiceCurrency, targetCurrency, company, invoice) => {
	// Rounded even when no conversion is needed, so the two-decimal guarantee holds on every path.
	// The ledger column has two decimals regardless of whether a rate was involved.
	if (targetCurrency === invoice.currency) {
		return Math.round(priceInInvoiceCurrency * 100) / 100;
	}

	const getRate = resolveRateForInvoice(company, invoice);

	const priceInCompanyCurrency =
		invoice.currency !== company.currency ? priceInInvoiceCurrency / getRate(invoice.currency) : priceInInvoiceCurrency;
	const priceInTargetCurrency =
		targetCurrency !== company.currency ? priceInCompanyCurrency * getRate(targetCurrency) : priceInCompanyCurrency;

	return Math.round(priceInTargetCurrency * 100) / 100;
};

export const addQuantityDecimals = (value, company) => {
	return value.toFixed(company.invoiceQuantityDecimals || 2);
};
